import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAcl } from '@/hooks/useAcl';
import { availableRoles } from '@/mocks/accessControl';
import { ALL_FEATURES, ALL_PERMISSIONS } from '@/mocks/aclPermissions';
import type { AclFeature, AclPermission } from '@/mocks/aclPermissions';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';

interface RolePermissionsPanelProps {
  onSaved?: () => void;
}

export default function RolePermissionsPanel({ onSaved }: RolePermissionsPanelProps) {
  const { t } = useTranslation();
  const { acl, updateRolePermissions } = useAcl();

  const [selectedRole, setSelectedRole] = useState<string>(availableRoles[0]);
  const [dirty, setDirty] = useState(false);
  const [localPerms, setLocalPerms] = useState<Partial<Record<AclFeature, AclPermission[]>>>(() => {
    const rolePerms = acl[availableRoles[0]];
    return rolePerms ? { ...rolePerms } : {};
  });

  // Switch role
  const handleRoleChange = useCallback(
    (role: string) => {
      setSelectedRole(role);
      const rolePerms = acl[role];
      setLocalPerms(rolePerms ? { ...rolePerms } : {});
      setDirty(false);
    },
    [acl],
  );

  // Toggle a single permission
  const handleToggle = useCallback(
    (feature: AclFeature, permission: AclPermission) => {
      setLocalPerms((prev) => {
        const current = prev[feature] ?? [];
        const has = current.includes(permission);
        let updated: AclPermission[];
        if (has) {
          updated = current.filter((p) => p !== permission);
        } else {
          updated = [...current, permission];
        }
        return { ...prev, [feature]: updated };
      });
      setDirty(true);
    },
    [],
  );

  // Toggle all permissions for a feature
  const handleToggleFeature = useCallback(
    (feature: AclFeature) => {
      setLocalPerms((prev) => {
        const current = prev[feature] ?? [];
        const allOn = ALL_PERMISSIONS.length > 0 && current.length >= ALL_PERMISSIONS.length;
        return {
          ...prev,
          [feature]: allOn ? [] : ALL_PERMISSIONS.map((p) => p.perm),
        };
      });
      setDirty(true);
    },
    [],
  );

  // Save changes
  const handleSave = useCallback(() => {
    Object.entries(localPerms).forEach(([feature, permissions]) => {
      updateRolePermissions(selectedRole, feature as AclFeature, permissions ?? []);
    });
    setDirty(false);
    onSaved?.();
  }, [localPerms, selectedRole, updateRolePermissions, onSaved]);

  // Reset to current ACL state
  const handleReset = useCallback(() => {
    const rolePerms = acl[selectedRole];
    setLocalPerms(rolePerms ? { ...rolePerms } : {});
    setDirty(false);
  }, [acl, selectedRole]);

  // Count active features
  const activeFeatures = useMemo(() => {
    return Object.values(localPerms).filter((p) => p && p.length > 0).length;
  }, [localPerms]);

  return (
    <div className="space-y-6">
      {/* Role selector + info bar */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="flex-1 max-w-xs">
          <label className="block text-sm font-label font-medium text-foreground-700 mb-1.5">
            {t('acl.selectRoleToEdit')}
          </label>
          <select
            value={selectedRole}
            onChange={(e) => handleRoleChange(e.target.value)}
            className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 font-label focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors cursor-pointer"
          >
            {availableRoles.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          <Badge variant="secondary" size="sm">
            {activeFeatures} / {ALL_FEATURES.length} {t('acl.featuresEnabled')}
          </Badge>
          {dirty && (
            <>
              <Button variant="ghost" size="sm" onClick={handleReset}>
                {t('common.reset')}
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave} iconLeft="ri-save-line">
                {t('acl.savePermissions')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Desc */}
      <p className="text-xs text-foreground-500 font-label">
        {t('acl.rolePermissionHint', { role: selectedRole })}
      </p>

      {/* Permissions header */}
      <div className="hidden md:grid md:grid-cols-[220px_1fr] gap-4">
        <div />
        <div className="grid grid-cols-5 gap-2">
          {ALL_PERMISSIONS.map((p) => (
            <div key={p.perm} className="text-center">
              <span className="text-xs font-label font-semibold text-foreground-600">{t(p.labelKey)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Feature rows */}
      <div className="flex flex-col gap-1">
        {ALL_FEATURES.map((f) => {
          const perms = localPerms[f.feature] ?? [];
          const allOn = ALL_PERMISSIONS.length > 0 && perms.length >= ALL_PERMISSIONS.length;
          const anyOn = perms.length > 0;

          return (
            <div
              key={f.feature}
              className={`rounded-xl border transition-colors ${
                anyOn
                  ? 'bg-primary-50/40 border-primary-200'
                  : 'bg-background-50 border-background-200/70'
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-3 p-3 md:p-4">
                {/* Feature name */}
                <div className="flex items-center gap-3 md:w-[208px] flex-shrink-0">
                  <button
                    onClick={() => handleToggleFeature(f.feature)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-background-300 bg-background-50 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
                    title={allOn ? t('acl.removeAll') : t('acl.grantAll')}
                  >
                    <i className={`${allOn ? 'ri-checkbox-circle-fill text-primary-500' : 'ri-checkbox-blank-circle-line text-foreground-400'} text-base`} />
                  </button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <i className={`${f.icon} text-base ${anyOn ? 'text-primary-600' : 'text-foreground-400'}`} />
                      <span className={`text-sm font-label font-medium truncate ${anyOn ? 'text-foreground-900' : 'text-foreground-400'}`}>
                        {t(f.labelKey)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Permission checkboxes */}
                <div className="grid grid-cols-5 gap-2 flex-1">
                  {ALL_PERMISSIONS.map((p) => {
                    const checked = perms.includes(p.perm);
                    const disabled = p.perm === 'manage' && !perms.includes('view');
                    return (
                      <label
                        key={p.perm}
                        className={`
                          flex items-center justify-center gap-1.5 py-1.5 px-1 rounded-md cursor-pointer
                          transition-colors select-none
                          ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-background-100'}
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => handleToggle(f.feature, p.perm)}
                          className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-2 focus:ring-primary-400 focus:ring-offset-0 cursor-pointer accent-primary-500"
                        />
                        <span className="md:hidden text-xs font-label text-foreground-600">{t(p.labelKey)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom save bar */}
      {dirty && (
        <div className="sticky bottom-0 flex items-center justify-end gap-3 p-4 bg-background-50 border border-primary-200 rounded-xl shadow-raised">
          <p className="text-xs text-foreground-600 font-label flex-1">
            {t('acl.unsavedChanges')}
          </p>
          <Button variant="ghost" size="sm" onClick={handleReset}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} iconLeft="ri-save-line">
            {t('acl.savePermissions')}
          </Button>
        </div>
      )}
    </div>
  );
}